import { defineAsyncComponent, type Component } from "vue";
import {
  CONVERSATION_SETTINGS_SIDEBARS,
  CONVERSATION_SETTINGS_SIDEBAR_COMPONENTS,
} from "conversations/constants";
import {
  PORTFOLIO_SETTINGS_SIDEBARS,
  PORTFOLIO_SETTINGS_SIDEBAR_COMPONENTS,
} from "portfolio/constants";

export const SIDEBARS = {
  NOTIFICATIONS: {
    sidebar: "notifications",
  },
  FEATURES: {
    sidebar: "features",
  },
  STUDENT_ACTIVITY: {
    sidebar: "student-activity",
    paramKey: "sidebar-activity-id",
  },
  STUDENT: {
    sidebar: "student",
    paramKey: "sidebar-student-id",
    /**
     * The tab the profile opens on. Declared here so it travels with this drawer's stack
     * entry: the serializer wipes every `sidebar-*` key on each write and only puts back
     * what an entry declares, so an undeclared key would be lost the moment a drawer is
     * stacked on top — and one outside the prefix would outlive the drawer.
     */
    extraParamKeys: ["sidebar-student-view"],
  },
  HELP_FORM: {
    sidebar: "helpForm",
  },
  FEEDBACK_FORM: {
    sidebar: "feedbackForm",
  },
  STUDENT_ASSIGNMENT: {
    sidebar: "student-assignment",
    paramKey: "sidebar-assignment-id",
  },
  STUDENT_ACTIONPLAN: {
    sidebar: "student-actionplan",
    paramKey: "sidebar-actionplan-id",
  },
  MY_ACTIONPLAN: {
    sidebar: "my-actionplan",
    paramKey: "sidebar-my-actionplan-id",
  },
  MY_CONVERSATION: {
    sidebar: "my-conversation",
    paramKey: "sidebar-my-conversation-id",
  },
  TEACHER_CONVERSATION: {
    sidebar: "teacher-conversation",
    paramKey: "sidebar-conversation-id",
  },
  TEACHER_ACTIVITY: {
    sidebar: "teacher-activity",
    paramKey: "sidebar-activity-id",
    extraParamKeys: ["sidebar-student-id"],
  },
  MEETING_PLANNING: {
    sidebar: "meeting-planning",
    paramKey: "sidebar-meeting-id",
  },
  MEETING_TEACHER_RATINGS: {
    sidebar: "meeting-teacher-ratings",
    paramKey: "sidebar-teacher-shortage",
    extraParamKeys: ["sidebar-meeting-id"],
  },
  MEETING_SHARED_FEEDBACK: {
    sidebar: "meeting-shared-feedback",
    paramKey: "sidebar-teacher-shortage",
    extraParamKeys: ["sidebar-meeting-id"],
  },
  SCHOOL_FORM_GUIDE: {
    sidebar: "school-form-guide",
  },
  // Portfolio Settings
  ...PORTFOLIO_SETTINGS_SIDEBARS,
  ...CONVERSATION_SETTINGS_SIDEBARS,
} as const;

export type SidebarEntry = (typeof SIDEBARS)[keyof typeof SIDEBARS];
export type SidebarValue = SidebarEntry["sidebar"];

interface SidebarConfig {
  component: Component;
}

export const SIDEBAR_COMPONENTS: Record<SidebarValue, SidebarConfig> = {
  [SIDEBARS.NOTIFICATIONS.sidebar]: {
    component: defineAsyncComponent(
      () => import("components/notifications/NotificationsDrawer.vue"),
    ),
  },
  [SIDEBARS.FEATURES.sidebar]: {
    component: defineAsyncComponent(
      () => import("components/features/FeaturesDrawer.vue"),
    ),
  },
  [SIDEBARS.STUDENT_ACTIVITY.sidebar]: {
    component: defineAsyncComponent(
      () => import("portfolio/components/activities/StudentActivityDrawer.vue"),
    ),
  },
  [SIDEBARS.STUDENT.sidebar]: {
    component: defineAsyncComponent(
      () => import("components/modals/students/StudentInfoDrawer.vue"),
    ),
  },
  [SIDEBARS.HELP_FORM.sidebar]: {
    component: defineAsyncComponent(
      () => import("pages/knowledge-base/HelpFormDrawer.vue"),
    ),
  },
  [SIDEBARS.FEEDBACK_FORM.sidebar]: {
    component: defineAsyncComponent(
      () => import("pages/knowledge-base/FeedbackFormDrawer.vue"),
    ),
  },
  [SIDEBARS.STUDENT_ASSIGNMENT.sidebar]: {
    component: defineAsyncComponent(
      () =>
        import("portfolio/components/assignments/StudentAssignmentDrawer.vue"),
    ),
  },
  [SIDEBARS.STUDENT_ACTIONPLAN.sidebar]: {
    component: defineAsyncComponent(
      () => import("actionplan/components/ActionplanDrawer.vue"),
    ),
  },
  [SIDEBARS.MY_ACTIONPLAN.sidebar]: {
    component: defineAsyncComponent(
      () => import("actionplan/components/StudentActionplanDrawer.vue"),
    ),
  },
  [SIDEBARS.MY_CONVERSATION.sidebar]: {
    component: defineAsyncComponent(
      () =>
        import("conversations/components/student/StudentConversationDrawer.vue"),
    ),
  },
  [SIDEBARS.TEACHER_CONVERSATION.sidebar]: {
    component: defineAsyncComponent(
      () =>
        import("conversations/components/teacher/TeacherConversationDrawer.vue"),
    ),
  },
  [SIDEBARS.TEACHER_ACTIVITY.sidebar]: {
    component: defineAsyncComponent(
      () => import("portfolio/components/activities/TeacherActivityDrawer.vue"),
    ),
  },
  [SIDEBARS.MEETING_PLANNING.sidebar]: {
    component: defineAsyncComponent(
      () =>
        import("meetings/advisory/components/teacher/MeetingPlanningDrawer.vue"),
    ),
  },
  [SIDEBARS.MEETING_TEACHER_RATINGS.sidebar]: {
    component: defineAsyncComponent(
      () =>
        import("meetings/student-meeting/components/teacher/manage/MeetingTeacherRatingsDrawer.vue"),
    ),
  },
  [SIDEBARS.MEETING_SHARED_FEEDBACK.sidebar]: {
    component: defineAsyncComponent(
      () =>
        import("meetings/student-meeting/components/teacher/manage/MeetingSharedFeedbackDrawer.vue"),
    ),
  },
  [SIDEBARS.SCHOOL_FORM_GUIDE.sidebar]: {
    component: defineAsyncComponent(
      () => import("core/schools/components/SchoolFormGuideDrawer.vue"),
    ),
  },
  // Portfolio Settings
  ...PORTFOLIO_SETTINGS_SIDEBAR_COMPONENTS,
  // Conversation Settings
  ...CONVERSATION_SETTINGS_SIDEBAR_COMPONENTS,
};
